import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DashboardItem } from '@client/components/_ui/display/dashboard/DashboardItem';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { FilterBar, useFilterBuilderWithUrl } from '@client/components/filters';
import { CHART_COLORS } from '@client/design-system/__generated__';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { ShieldCheck, UserRoundPen } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { MasterTrackingListDrawer } from './MasterTrackingListDrawer';
import { AutoDetectTile } from './tiles/AutoDetectTile';
import { ControlsTile } from './tiles/ControlsTile';
import { CoverageQualityTile } from './tiles/CoverageQualityTile';
import { FlagDonutTile } from './tiles/FlagDonutTile';
import { OrgBarTile, toOrgBars } from './tiles/OrgBarTile';
import { extractOrgFilters, useComplianceFilterConfig } from './useComplianceFilterConfig';

const ComplianceSearchSchema = z.object({
    'compliance-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('org'),
                    operator: FilterOperatorSchema,
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/admin-console/monitoring/compliance')({
    staticData: {
        permissions: ['admin_orgs:read', 'admin_users:read', 'admin_system:read', 'admin_ai:read'],
        breadcrumb: {
            title: 'Compliance',
        },
        ui: {
            icon: ShieldCheck,
            title: 'Compliance',
            subtitle: 'Compliance metrics across the platform',
            widthVariant: 'fullWidth',
            menuDescription: 'Monitor compliance metrics by organisation',
        },
    },
    validateSearch: zodValidator(ComplianceSearchSchema),
    component: ComplianceMonitoringPage,
});

const WINDOW = 'last 30 days';
const ALL_TIME = 'all-time (set up)';

/**
 * Orgs with no creations in the window come back as `medianMs: null` (no data) —
 * omit their bars rather than rendering a misleading 0ms median.
 */
function toDurationBars(rows: { orgName: string; medianMs: number | null }[] | undefined) {
    return (rows ?? []).flatMap((r) => (r.medianMs === null ? [] : [{ orgName: r.orgName, value: r.medianMs }]));
}

/** Format a millisecond duration as a compact "Xm Ys" (or "Ys" under a minute) for the time-to-create axis. */
function formatDurationMs(ms: number): string {
    if (ms <= 0) return '0s';
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
}

function ComplianceMonitoringPage() {
    const { trpc } = useTrpc();
    const searchParams = Route.useSearch();
    const { config: filterConfig } = useComplianceFilterConfig();
    const [dataSourcesOpen, setDataSourcesOpen] = useState(false);

    const filterState = useFilterBuilderWithUrl(filterConfig, 'compliance-filters', searchParams['compliance-filters']);

    const orgFilters = extractOrgFilters(filterState.whereClause);

    const { data, isLoading, error, refetch } =
        trpc.admin.complianceMonitoring.complianceDashboard.useQuery(orgFilters);

    return (
        <PageController route={Route}>
            <TableToolbar
                variant="enhanced"
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={filterState.filters}
                        whereClause={filterState.whereClause}
                        onAddFilter={filterState.addFilter}
                        onUpdateFilter={filterState.updateFilter}
                        onRemoveFilter={filterState.removeFilter}
                        onClearFilters={filterState.clearFilters}
                    />
                }
                actions={
                    <Button.Icon
                        icon={<UserRoundPen />}
                        filled
                        tooltip="Data sources"
                        onClick={() => setDataSourcesOpen(true)}
                    />
                }
            />

            <MasterTrackingListDrawer open={dataSourcesOpen} onClose={() => setDataSourcesOpen(false)} />

            {error ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    title="Couldn't load compliance metrics"
                />
            ) : isLoading ? (
                <Loading />
            ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <DashboardItem title="Incidents logged" subtitle={`Per organisation · ${WINDOW}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.blue}
                            data={toOrgBars(data?.incidentsLogged.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem title="Complaints logged" subtitle={`Per organisation · ${WINDOW}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.cyan}
                            data={toOrgBars(data?.complaintsLogged.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Incidents with a linked complaint"
                        subtitle={`Share of incidents linked to a complaint · ${WINDOW}`}
                    >
                        <OrgBarTile
                            percent
                            loading={isLoading}
                            color={CHART_COLORS.purple}
                            data={toOrgBars(data?.incidentsWithLinkedComplaint.perOrg, (r) => Math.round(r.pct))}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Complaints with a linked incident"
                        subtitle={`Share of complaints linked to an incident · ${WINDOW}`}
                    >
                        <OrgBarTile
                            percent
                            loading={isLoading}
                            color={CHART_COLORS.purple}
                            data={toOrgBars(data?.complaintsWithLinkedIncident.perOrg, (r) => Math.round(r.pct))}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Incidents analysed and breaches actioned"
                        subtitle={`Share fully analysed with no unresolved breaches · ${WINDOW}`}
                    >
                        <OrgBarTile
                            percent
                            loading={isLoading}
                            color={CHART_COLORS.green}
                            data={toOrgBars(data?.incidentsAnalysedActioned.perOrg, (r) => Math.round(r.pct))}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Incidents description quality"
                        subtitle={`Share whose description covered all AI checks · ${WINDOW}`}
                    >
                        <CoverageQualityTile
                            entity="incident"
                            loading={isLoading}
                            color={CHART_COLORS.green}
                            data={data?.incidentsQuality}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Complaints description quality"
                        subtitle={`Share whose description covered all AI checks · ${WINDOW}`}
                    >
                        <CoverageQualityTile
                            entity="complaint"
                            loading={isLoading}
                            color={CHART_COLORS.cyan}
                            data={data?.complaintsQuality}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Breaches confirmed"
                        subtitle={`Confirmed breaches per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.red}
                            data={toOrgBars(data?.breachesConfirmed.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Obligations register"
                        subtitle={`Share of incidents with an obligation linked · ${WINDOW}`}
                    >
                        <OrgBarTile
                            percent
                            loading={isLoading}
                            color={CHART_COLORS.purple}
                            data={toOrgBars(data?.obligationsRegister.perOrg, (r) => Math.round(r.pct))}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Controls and tests"
                        subtitle="Controls defined (all-time) and tests run (last 30 days)"
                    >
                        <ControlsTile data={data?.controlTests} loading={isLoading} />
                    </DashboardItem>

                    <DashboardItem title="Risk register" subtitle={`Risk records per organisation · ${ALL_TIME}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.orange}
                            data={toOrgBars(data?.riskRegister.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Risk appetite"
                        subtitle={`Appetite statements per organisation · ${ALL_TIME}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.yellow}
                            data={toOrgBars(data?.riskAppetite.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Conflicts of interest"
                        subtitle={`Conflict records per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.orange}
                            data={toOrgBars(data?.conflicts.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem title="Training and CPD" subtitle={`Training records per organisation · ${WINDOW}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.indigo}
                            data={toOrgBars(data?.training.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem title="CPD target" subtitle="Current-period CPD points target per organisation">
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.indigo}
                            data={toOrgBars(data?.cpdTarget.perOrg, (r) => r.value ?? 0)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Material service providers"
                        subtitle={`MSP records per organisation · ${ALL_TIME}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.indigo}
                            data={toOrgBars(data?.msp.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem title="Website checker" subtitle={`Scans run per organisation · ${WINDOW}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.cyan}
                            data={toOrgBars(data?.websiteChecks.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Document analyser"
                        subtitle={`Documents analysed per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.cyan}
                            data={toOrgBars(data?.documentAnalyses.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem title="PDS compare" subtitle={`Comparisons run per organisation · ${WINDOW}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.cyan}
                            data={toOrgBars(data?.pdsComparisons.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem title="Dashboards viewed" subtitle={`Dashboard views per organisation · ${WINDOW}`}>
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.blue}
                            data={toOrgBars(data?.dashboardsViewed.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Reports generated"
                        subtitle={`Reports generated per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.green}
                            data={toOrgBars(data?.reportsGenerated.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Workload tabs viewed"
                        subtitle={`Workload tab views per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.indigo}
                            data={toOrgBars(data?.workloadTabsViewed.perOrg, (r) => r.count)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Incident time to create"
                        subtitle={`Median form-fill time per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.orange}
                            valueFormatter={formatDurationMs}
                            data={toDurationBars(data?.incidentTimeToCreate.perOrg)}
                        />
                    </DashboardItem>

                    <DashboardItem
                        title="Complaint time to create"
                        subtitle={`Median form-fill time per organisation · ${WINDOW}`}
                    >
                        <OrgBarTile
                            loading={isLoading}
                            color={CHART_COLORS.orange}
                            valueFormatter={formatDurationMs}
                            data={toDurationBars(data?.complaintTimeToCreate.perOrg)}
                        />
                    </DashboardItem>

                    <DashboardItem title="Auto-detect" subtitle="Organisations with auto-detect intake enabled">
                        <AutoDetectTile data={data?.autoDetectActive} loading={isLoading} />
                    </DashboardItem>

                    <DashboardItem title="Compliance audit" subtitle="Organisations with at least one compliance audit">
                        <FlagDonutTile
                            loading={isLoading}
                            enabled={data?.complianceAuditActive.enabled ?? 0}
                            total={data?.complianceAuditActive.total ?? 0}
                            activeLabel="Has audit"
                            inactiveLabel="None"
                        />
                    </DashboardItem>
                </div>
            )}
        </PageController>
    );
}
